import { useNavigate } from "react-router"
import { useCallback } from "react"
import type { ViewType } from "@/types/reports"

/**
 * Maps ViewType state strings to URL paths.
 */
function viewTypeToPath(view: ViewType): string {
	const staticRoutes: Record<string, string> = {
		dashboard: "/",
		"tender-assistant": "/tender-assistant",
		tenders: "/tenders",
		"search-reports": "/search",
		templates: "/templates",
		"new-template": "/templates/new",
		"section-library": "/section-library",
		"public-tenders": "/public-tenders",
		"public-tenders-organizations": "/public-tenders/organizations",
		"procurement-templates": "/procurement-templates",
		"evaluations-new": "/evaluations/new",
		organization: "/organization",
		users: "/organization/users",
		teams: "/organization/teams",
		"role-labels": "/settings/role-labels",
		audit: "/admin/audit",
		settings: "/settings",
		brand: "/brand",
		admin: "/admin",
	}

	if (staticRoutes[view]) return staticRoutes[view]

	// Dynamic views — order matters: most specific prefix first
	if (view.startsWith("procurement-template-doc-")) {
		// Format: procurement-template-doc-{templateId}:{reportId}
		const payload = view.substring("procurement-template-doc-".length)
		const [templateId, reportId] = payload.split(":")
		return `/procurement-templates/${templateId}/editor?doc=${reportId}`
	}
	if (view.startsWith("procurement-template-")) {
		const id = view.substring("procurement-template-".length)
		return `/procurement-templates/${id}`
	}
	if (view.startsWith("public-tender-")) {
		const id = view.substring("public-tender-".length)
		return `/public-tenders/${id}`
	}
	if (view.startsWith("bundle-")) {
		const id = view.substring("bundle-".length)
		return `/bundles/${id}`
	}
	if (view.startsWith("template-group-")) {
		const groupId = view.substring("template-group-".length)
		return `/templates/group/${groupId}`
	}
	if (view.startsWith("template-edit-")) {
		const id = view.substring("template-edit-".length)
		return `/templates/${id}/edit`
	}
	if (view.startsWith("template-")) {
		const id = view.substring("template-".length)
		return `/templates/${id}`
	}
	if (view.startsWith("expedient-")) {
		const id = view.substring("expedient-".length)
		return `/expedients/${id}`
	}
	if (view.startsWith("report-")) {
		const id = view.substring("report-".length)
		return `/reports/${id}`
	}
	return "/"
}

/**
 * Converts a URL pathname to the equivalent ViewType string.
 */
export function pathToViewType(pathname: string): ViewType {
	const staticMap: Record<string, ViewType> = {
		"/": "dashboard",
		"/tenders": "tenders",
		"/search": "search-reports",
		"/templates": "templates",
		"/templates/new": "new-template",
		"/organization": "organization",
		"/organization/users": "users",
		"/organization/teams": "teams",
		"/users": "users",
		"/teams": "teams",
		"/settings/role-labels": "role-labels",
		"/admin/audit": "audit",
		"/settings": "settings",
		"/brand": "brand",
		"/section-library": "section-library",
		"/tender-assistant": "tender-assistant",
		"/admin": "admin",
		"/public-tenders": "public-tenders",
		"/public-tenders/organizations": "public-tenders-organizations",
		"/procurement-templates": "procurement-templates",
		"/evaluations/new": "evaluations-new",
	}

	if (staticMap[pathname]) return staticMap[pathname]

	const procTemplateDocMatch = pathname.match(/^\/procurement-templates\/([^/]+)\/documents\/([^/]+)$/)
	if (procTemplateDocMatch) return `procurement-template-doc-${procTemplateDocMatch[1]}:${procTemplateDocMatch[2]}` as ViewType

	const procTemplateMatch = pathname.match(/^\/procurement-templates\/([^/]+)$/)
	if (procTemplateMatch) return `procurement-template-${procTemplateMatch[1]}`

	const publicTenderMatch = pathname.match(/^\/public-tenders\/([^/]+)$/)
	if (publicTenderMatch && publicTenderMatch[1] !== "organizations")
		return `public-tender-${publicTenderMatch[1]}`

	const bundleMatch = pathname.match(/^\/bundles\/([^/]+)$/)
	if (bundleMatch) return `bundle-${bundleMatch[1]}`

	const templateGroupMatch = pathname.match(/^\/templates\/group\/([^/]+)$/)
	if (templateGroupMatch) return `template-group-${templateGroupMatch[1]}`

	const templateEditMatch = pathname.match(/^\/templates\/([^/]+)\/edit$/)
	if (templateEditMatch) return `template-edit-${templateEditMatch[1]}`

	const templateDetailMatch = pathname.match(/^\/templates\/([^/]+)$/)
	if (templateDetailMatch) return `template-${templateDetailMatch[1]}`

	const reportMatch = pathname.match(/^\/reports\/([^/]+)$/)
	if (reportMatch) return `report-${reportMatch[1]}`

	const expedientMatch = pathname.match(/^\/expedients\/([^/]+)$/)
	if (expedientMatch) return `expedient-${expedientMatch[1]}`

	return "dashboard"
}

/**
 * Maps sidebar navigation item IDs to URL paths.
 */
export function sidebarIdToPath(id: string): string {
	return viewTypeToPath(id as ViewType)
}

export function useAppRouter() {
	const nav = useNavigate()

	const navigate = useCallback(
		(view: ViewType) => {
			nav(viewTypeToPath(view))
		},
		[nav]
	)

	const goBack = useCallback(() => {
		nav(-1)
	}, [nav])

	return { navigate, goBack }
}
